import { useState, useEffect, useCallback, useMemo } from "react";
import { Rocket, Shield, TrendingUp, RefreshCw, Zap, AlertTriangle, Globe, ArrowUpDown, Flame, Target, ChevronRight, Activity, BarChart3, Crosshair, Trophy, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  { id: "ALL", label: "Tous" },
  { id: "US", label: "US", flag: "🇺🇸" },
  { id: "EU", label: "Europe", flag: "🇪🇺" },
  { id: "ASIA", label: "Asie", flag: "🇯🇵" },
];

const SORTS = [
  { id: "rr", label: "R/R", icon: Target },
  { id: "score", label: "Score", icon: BarChart3 },
  { id: "volume", label: "Volume", icon: Activity },
  { id: "change", label: "% Jour", icon: TrendingUp },
];

const CAP_BADGE = {
  SMALL: { label: "Small Cap", short: "SC", color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  MID: { label: "Mid Cap", short: "MC", color: "text-violet-400 bg-violet-500/10 border-violet-500/20" },
  LARGE: { label: "Large Cap", short: "LC", color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
};

const isHot = (entry) => entry.vol_ratio >= 2.0 && entry.rsi >= 50;

const Top5MomentumCard = ({ items, onAnalyze }) => {
  if (!items || items.length === 0) return null;
  const top5 = items.slice(0, 5);
  return (
    <div className="glass-card rounded-xl p-4 mb-6 border border-cyan-500/20 relative overflow-hidden" data-testid="top5-momentum-du-jour">
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/[0.03] via-transparent to-emerald-500/[0.02]" />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-amber-400" />
          <h3 className="text-sm font-black text-white uppercase tracking-wider">Top 10 Momentum du Jour</h3>
          <span className="text-[9px] text-slate-500 ml-auto">Entry + Targets</span>
        </div>
        <div className="space-y-2">
          {top5.map((item, i) => {
            const setupLabel = item.has_breakout ? "break" : item.setup === "pivot" ? "pivot" : item.setup || "setup";
            const setupColor = item.has_breakout
              ? "text-emerald-400 bg-emerald-500/15 border-emerald-500/30"
              : item.setup === "pivot"
              ? "text-cyan-400 bg-cyan-500/15 border-cyan-500/30"
              : "text-violet-400 bg-violet-500/15 border-violet-500/30";
            const targets = item.targets || [];
            const rankColors = ["text-amber-400", "text-slate-300", "text-orange-500", "text-slate-400", "text-slate-500"];
            const rrColor = item.risk_reward >= 3 ? "text-emerald-400" : item.risk_reward >= 2 ? "text-cyan-400" : "text-white";

            return (
              <div
                key={item.ticker}
                className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-700/30 bg-slate-800/20 hover:bg-slate-700/30 hover:border-cyan-500/20 transition-all cursor-pointer group"
                onClick={() => onAnalyze?.(item.ticker)}
                data-testid={`top5-momentum-${item.ticker}`}
              >
                <span className={`text-lg font-black w-6 shrink-0 ${rankColors[i]}`}>{i + 1}</span>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="font-mono text-sm font-black text-cyan-400 group-hover:text-cyan-300 shrink-0">{item.ticker}</span>
                  <span className="text-[10px] text-slate-500 truncate hidden sm:inline">{item.name}</span>
                </div>
                <span className="font-mono text-sm font-bold text-white shrink-0">${item.price}</span>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border shrink-0 ${setupColor}`}>{setupLabel}</span>
                {item.risk_reward > 0 && (
                  <span className={`text-[9px] font-mono font-bold shrink-0 ${rrColor}`}>R/R {item.risk_reward}</span>
                )}
                <div className="hidden sm:flex items-center gap-1 shrink-0">
                  <ArrowRight className="w-3 h-3 text-emerald-500/50" />
                  {targets.slice(0, 4).map((t, j) => (
                    <span key={j} className="text-[10px] font-mono text-emerald-400/80 bg-emerald-500/8 px-1.5 py-0.5 rounded">${t}</span>
                  ))}
                  {targets.length > 4 && <span className="text-[9px] text-emerald-400/50">+</span>}
                </div>
                {item.vol_ratio >= 2 && (
                  <Flame className="w-3 h-3 text-orange-400 animate-pulse shrink-0" />
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3 mt-3 pt-2 border-t border-slate-700/20 text-[9px] text-slate-500">
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> break = breakout confirmé</span>
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400" /> pivot = rebond sur niveau clé</span>
          <span className="flex items-center gap-1"><Flame className="w-2.5 h-2.5 text-orange-400" /> HOT = vol x2+ & RSI 50+</span>
        </div>
      </div>
    </div>
  );
};

const RocketCard = ({ item, onAnalyze }) => {
  const intensity = item.change_pct >= 50 ? "from-rose-500/15 to-rose-500/5 border-rose-500/20" 
    : item.change_pct >= 20 ? "from-orange-500/15 to-orange-500/5 border-orange-500/20" 
    : "from-emerald-500/10 to-emerald-500/5 border-emerald-500/20";
  const textColor = item.change_pct >= 50 ? "text-rose-400" : item.change_pct >= 20 ? "text-orange-400" : "text-emerald-400";

  return (
    <div
      className={`group relative rounded-xl border bg-gradient-to-br ${intensity} p-4 hover:scale-[1.01] transition-all cursor-pointer`}
      onClick={() => onAnalyze?.(item.ticker)}
      data-testid={`rocket-${item.ticker}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-lg font-black text-white">{item.ticker}</span>
            {item.vol_ratio >= 2 && (
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 border border-orange-500/30 flex items-center gap-0.5 animate-pulse">
                <Flame className="w-3 h-3" />HOT
              </span>
            )}
          </div>
          <span className="text-xs text-slate-500 block truncate max-w-[180px]">{item.name}</span>
        </div>
        <div className="text-right">
          <div className={`font-mono text-2xl font-black ${textColor} flex items-center gap-0.5`}>
            +{item.change_pct}%
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-700/30">
        <div>
          <div className="text-[9px] text-slate-600 uppercase mb-0.5">Prix</div>
          <div className="text-sm font-mono font-bold text-white">${item.price}</div>
        </div>
        <div>
          <div className="text-[9px] text-slate-600 uppercase mb-0.5">Volume</div>
          <div className={`text-sm font-mono font-bold ${item.vol_ratio >= 2 ? "text-amber-400" : "text-slate-400"}`}>{item.vol_ratio}x</div>
        </div>
        <div>
          <div className="text-[9px] text-slate-600 uppercase mb-0.5">H/L</div>
          <div className="text-xs font-mono text-slate-400">${item.day_high}/{item.day_low}</div>
        </div>
      </div>
      <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-700 group-hover:text-slate-500 transition-colors" />
    </div>
  );
};

const PreMarketCard = ({ item, onAnalyze }) => {
  const targets = item.targets || [];
  const setupColor = item.setup === "break"
    ? "text-emerald-400 bg-emerald-500/15 border-emerald-500/25"
    : "text-cyan-400 bg-cyan-500/15 border-cyan-500/25";
  const setupLabel = item.setup === "break" ? "BREAK" : "MOMENTUM";

  return (
    <div
      className="group relative rounded-xl border border-violet-500/20 bg-gradient-to-br from-violet-500/[0.06] to-violet-500/[0.02] p-4 hover:border-violet-500/30 hover:bg-violet-500/[0.08] transition-all cursor-pointer"
      onClick={() => onAnalyze?.(item.ticker)}
      data-testid={`premarket-${item.ticker}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-base font-black text-violet-300 group-hover:text-violet-200 transition-colors">{item.ticker}</span>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${setupColor}`}>{setupLabel}</span>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border text-slate-400 bg-slate-700/40 border-slate-600/30">{item.cap_label}</span>
            {item.vol_ratio >= 3 && (
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 border border-orange-500/30 flex items-center gap-0.5 animate-pulse">
                <Flame className="w-3 h-3" />HOT
              </span>
            )}
          </div>
          <span className="text-xs text-slate-500 block truncate max-w-[180px]">{item.name}</span>
        </div>
        <div className="text-right">
          <div className="font-mono text-xl font-black text-violet-300">+{item.change_pct}%</div>
          <div className="text-sm font-mono text-white">${item.price}</div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 py-3 border-y border-slate-800/30">
        <div>
          <div className="text-[9px] text-slate-600 uppercase mb-0.5 flex items-center gap-1">
            <Shield className="w-3 h-3 text-red-500/40" />Stop
          </div>
          <div className="text-sm font-mono font-bold text-red-400/80">${item.stop_loss}</div>
        </div>
        <div>
          <div className="text-[9px] text-slate-600 uppercase mb-0.5">R/R</div>
          <div className={`text-sm font-mono font-bold ${item.risk_reward >= 3 ? "text-emerald-400" : item.risk_reward >= 2 ? "text-cyan-400" : "text-white"}`}>{item.risk_reward}</div>
        </div>
        <div>
          <div className="text-[9px] text-slate-600 uppercase mb-0.5">Volume</div>
          <div className={`text-sm font-mono font-bold ${item.vol_ratio >= 3 ? "text-amber-400" : "text-slate-400"}`}>{item.vol_ratio}x</div>
        </div>
        <div>
          <div className="text-[9px] text-slate-600 uppercase mb-0.5">MktCap</div>
          <div className="text-xs font-mono text-slate-400">{item.market_cap >= 1e9 ? `${(item.market_cap/1e9).toFixed(1)}B` : `${(item.market_cap/1e6).toFixed(0)}M`}</div>
        </div>
      </div>

      {targets.length > 0 && (
        <div className="pt-3 flex items-center gap-2 flex-wrap">
          <span className="text-[9px] text-emerald-500 font-bold uppercase">Targets</span>
          {targets.slice(0, 5).map((t, i) => (
            <span key={i} className="text-xs font-mono text-emerald-300/70 bg-emerald-500/8 px-2 py-0.5 rounded">${t}</span>
          ))}
        </div>
      )}
      <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-700 group-hover:text-slate-500 transition-colors" />
    </div>
  );
};


const SetupCard = ({ entry, onAnalyze, type }) => {
  const targets = entry.targets || [];
  const isBreak = entry.has_breakout;
  const hot = isHot(entry);
  const setupLabel = isBreak ? "BREAKOUT" : entry.setup === "pivot" ? "PIVOT" : "RANGE";
  const setupColor = isBreak
    ? "text-emerald-400 bg-emerald-500/15 border-emerald-500/25"
    : entry.setup === "pivot"
    ? "text-cyan-400 bg-cyan-500/15 border-cyan-500/25"
    : "text-slate-400 bg-slate-700/50 border-slate-600/30";
  const regionFlag = entry.region === "US" ? "🇺🇸" : entry.region === "EU" ? "🇪🇺" : "🇯🇵";
  const cap = CAP_BADGE[entry.cap_size] || CAP_BADGE.LARGE;
  const rsiColor = entry.rsi >= 60 ? "text-emerald-400" : entry.rsi >= 50 ? "text-cyan-400" : entry.rsi <= 30 ? "text-red-400" : "text-slate-400";
  const rrColor = entry.risk_reward >= 3 ? "text-emerald-400" : entry.risk_reward >= 2 ? "text-cyan-400" : "text-white";

  return (
    <div
      className={`group relative rounded-xl border ${hot ? "border-amber-500/20 bg-amber-500/[0.03]" : "border-slate-800/40 bg-slate-900/40"} p-4 hover:border-slate-700/60 hover:bg-slate-800/20 transition-all cursor-pointer`}
      onClick={() => onAnalyze?.(entry.ticker)}
      data-testid={`momentum-${entry.ticker}`}
    >
      {/* Top: Ticker + badges */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm opacity-60">{regionFlag}</span>
            <span className="font-mono text-base font-black text-cyan-400 group-hover:text-cyan-300 transition-colors">{entry.ticker}</span>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${setupColor}`}>{setupLabel}</span>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${cap.color}`}>{cap.short}</span>
            {hot && (
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded border border-orange-500/30 bg-orange-500/15 text-orange-400 flex items-center gap-0.5 animate-pulse" data-testid={`hot-badge-${entry.ticker}`}>
                <Flame className="w-3 h-3" />HOT
              </span>
            )}
          </div>
          {entry.pre_market_price > 0 && (
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${entry.pre_market_change >= 0 ? "text-emerald-400 bg-emerald-500/8" : "text-red-400 bg-red-500/8"}`}>
              PRE {entry.pre_market_change >= 0 ? "+" : ""}{entry.pre_market_change}%
            </span>
          )}
        </div>
        <div className="text-right">
          <div className="text-lg font-mono font-bold text-white">${entry.price}</div>
          <div className={`font-mono text-sm font-bold ${entry.change_1d >= 0 ? "text-emerald-400" : "text-red-400"}`}>
            {entry.change_1d >= 0 ? "+" : ""}{entry.change_1d}%
          </div>
        </div>
      </div>

      {/* Middle: Key metrics grid */}
      <div className="grid grid-cols-4 gap-3 py-3 border-y border-slate-800/30">
        <div>
          <div className="text-[9px] text-slate-600 uppercase mb-1 flex items-center gap-1">
            <Shield className="w-3 h-3 text-red-500/40" />Stop
          </div>
          <div className="text-sm font-mono font-bold text-red-400/80">${entry.stop_loss}</div>
        </div>
        <div>
          <div className="text-[9px] text-slate-600 uppercase mb-1 flex items-center gap-1">
            <Target className="w-3 h-3 text-emerald-500/40" />R/R
          </div>
          <div className={`text-sm font-mono font-bold ${rrColor}`}>{entry.risk_reward}</div>
        </div>
        <div>
          <div className="text-[9px] text-slate-600 uppercase mb-1">RSI</div>
          <div className={`text-sm font-mono font-bold ${rsiColor}`}>{entry.rsi}</div>
        </div>
        <div>
          <div className="text-[9px] text-slate-600 uppercase mb-1">Vol</div>
          <div className={`text-sm font-mono font-bold ${entry.vol_ratio >= 2 ? "text-amber-400" : "text-slate-500"}`}>{entry.vol_ratio}x</div>
        </div>
      </div>

      {/* Bottom: Targets */}
      {targets.length > 0 && (
        <div className="pt-3 flex items-center gap-2 flex-wrap">
          <span className="text-[9px] text-emerald-500 font-bold uppercase">Targets</span>
          {targets.map((t, i) => (
            <span key={i} className="text-xs font-mono text-emerald-300/70 bg-emerald-500/8 px-2 py-0.5 rounded">
              ${t}
            </span>
          ))}
        </div>
      )}
      <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-700 group-hover:text-slate-500 transition-colors" />
    </div>
  );
};

function sortItems(items, sortBy) {
  return [...items].sort((a, b) => {
    switch (sortBy) {
      case "rr": return (b.risk_reward || 0) - (a.risk_reward || 0);
      case "score": return (b.buy_score || 0) - (a.buy_score || 0);
      case "volume": return (b.vol_ratio || 0) - (a.vol_ratio || 0);
      case "change": return (b.change_1d || 0) - (a.change_1d || 0);
      default: return 0;
    }
  });
}

export default function MomentumWatchlist({ onAnalyze, cachedData, setCachedData }) {
  const [data, setData] = useState(cachedData || null);
  const [loading, setLoading] = useState(false);
  const [regionFilter, setRegionFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("rr");
  const [breakoutOnly, setBreakoutOnly] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/momentum-watchlist`);
      const result = await res.json();
      setData(result);
      if (setCachedData) setCachedData(result);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [setCachedData]);

  // Only fetch on first visit (no data yet)
  useEffect(() => { if (!data) fetchData(); }, []);

  const filtered = useMemo(() => {
    if (!data) return { momentum: [], swings: [] };
    const filter = (items) => {
      let f = regionFilter === "ALL" ? items : items.filter((e) => e.region === regionFilter);
      if (breakoutOnly) f = f.filter((e) => e.has_breakout);
      return sortItems(f, sortBy);
    };
    return {
      momentum: filter(data.day_momentum || []),
      swings: filter(data.swing_setups || []),
    };
  }, [data, regionFilter, sortBy, breakoutOnly]);

  const hotCount = useMemo(() => {
    if (!data) return 0;
    const all = [...(data.day_momentum || []), ...(data.swing_setups || [])];
    return all.filter(isHot).length;
  }, [data]);

  const stats = useMemo(() => {
    if (!data) return null;
    const all = [...(data.day_momentum || []), ...(data.swing_setups || [])];
    const breakouts = all.filter(e => e.has_breakout).length;
    const avgRR = all.length > 0 ? (all.reduce((s, e) => s + (e.risk_reward || 0), 0) / all.length).toFixed(1) : 0;
    const pm = (data.premarket_movers || []).length;
    return { total: all.length, breakouts, avgRR, hot: hotCount, premarket: pm };
  }, [data, hotCount]);

  if (loading && !data) {
    return (
      <div className="w-full px-4 py-6">
        <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl p-8 text-center">
          <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-400">Scan des marchés en cours...</p>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { date, total_scanned, stop_rule } = data;

  return (
    <div className="w-full" data-testid="momentum-watchlist-panel">

      {/* Header + Stats */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
              <Rocket className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Momentum Watchlist</h2>
              <p className="text-xs text-slate-500">{date} — {total_scanned} actions scannées</p>
            </div>
          </div>
          <button onClick={fetchData} disabled={loading} className="p-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:bg-slate-700/50 transition-colors" data-testid="refresh-momentum">
            <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Stats bar */}
        {stats && (
          <div className="grid grid-cols-5 gap-3 mb-5" data-testid="momentum-stats">
            <div className="glass-card rounded-xl p-3 border border-slate-800/50">
              <div className="text-[9px] text-slate-600 uppercase font-bold mb-1">Setups</div>
              <div className="text-xl font-bold text-white font-mono">{stats.total}</div>
            </div>
            <div className="glass-card rounded-xl p-3 border border-emerald-500/15">
              <div className="text-[9px] text-emerald-500 uppercase font-bold mb-1">Breakouts</div>
              <div className="text-xl font-bold text-emerald-400 font-mono">{stats.breakouts}</div>
            </div>
            <div className="glass-card rounded-xl p-3 border border-cyan-500/15">
              <div className="text-[9px] text-cyan-500 uppercase font-bold mb-1">R/R Moyen</div>
              <div className="text-xl font-bold text-cyan-400 font-mono">{stats.avgRR}</div>
            </div>
            <div className="glass-card rounded-xl p-3 border border-violet-500/15">
              <div className="text-[9px] text-violet-500 uppercase font-bold mb-1 flex items-center gap-1"><Crosshair className="w-3 h-3" />Small-Cap</div>
              <div className="text-xl font-bold text-violet-400 font-mono">{stats.premarket}</div>
            </div>
            <div className="glass-card rounded-xl p-3 border border-orange-500/15">
              <div className="text-[9px] text-orange-500 uppercase font-bold mb-1 flex items-center gap-1"><Flame className="w-3 h-3" />Hot</div>
              <div className="text-xl font-bold text-orange-400 font-mono">{stats.hot}</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Globe className="w-4 h-4 text-slate-600" />
            <div className="flex bg-slate-900/80 rounded-lg p-0.5 border border-slate-800/50">
              {REGIONS.map((r) => (
                <button key={r.id} onClick={() => setRegionFilter(r.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    regionFilter === r.id ? "bg-cyan-500/15 text-cyan-400 font-bold" : "text-slate-600 hover:text-slate-400"
                  }`} data-testid={`filter-region-${r.id.toLowerCase()}`}>
                  {r.flag ? `${r.flag} ${r.label}` : r.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <ArrowUpDown className="w-4 h-4 text-slate-600" />
            <div className="flex bg-slate-900/80 rounded-lg p-0.5 border border-slate-800/50">
              {SORTS.map((s) => (
                <button key={s.id} onClick={() => setSortBy(s.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                    sortBy === s.id ? "bg-violet-500/15 text-violet-400 font-bold" : "text-slate-600 hover:text-slate-400"
                  }`} data-testid={`sort-${s.id}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => setBreakoutOnly((p) => !p)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
              breakoutOnly
                ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
                : "bg-slate-900/80 text-slate-600 border-slate-800/50 hover:text-slate-400"
            }`}
            data-testid="filter-breakout-only"
          >
            <Target className="w-4 h-4" />
            Breakout Only
          </button>
        </div>
      </div>

      {/* TOP 5 MOMENTUM DU JOUR */}
      <Top5MomentumCard items={[...(data.day_momentum || []), ...(data.swing_setups || [])]} onAnalyze={onAnalyze} />

      {/* ROCKETS SECTION - Top Daily Movers */}
      {(data.rockets || []).length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 px-1">
            <Rocket className="w-4 h-4 text-rose-400" />
            <span className="text-sm font-bold text-rose-400 uppercase tracking-wider">Meilleures Perfs du Jour</span>
            <span className="text-[10px] text-slate-600 bg-slate-800 px-2 py-0.5 rounded-full ml-auto">{(data.rockets || []).length}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(data.rockets || []).map((r) => (
              <RocketCard key={r.ticker} item={r} onAnalyze={onAnalyze} />
            ))}
          </div>
        </div>
      )}

      {/* PRE-MARKET / MICRO-CAP MOVERS */}
      {(data.premarket_movers || []).length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 px-1">
            <Crosshair className="w-4 h-4 text-violet-400" />
            <span className="text-sm font-bold text-violet-400 uppercase tracking-wider">Small-Cap Movers</span>
            <span className="text-[10px] text-slate-600 bg-slate-800 px-2 py-0.5 rounded-full ml-auto">{(data.premarket_movers || []).length}</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {(data.premarket_movers || []).map((m) => (
              <PreMarketCard key={m.ticker} item={m} onAnalyze={onAnalyze} />
            ))}
          </div>
        </div>
      )}

      {filtered.momentum.length === 0 && filtered.swings.length === 0 && (data.rockets || []).length === 0 && (data.premarket_movers || []).length === 0 ? (
        <div className="py-16 text-center glass-card rounded-xl border border-slate-800/50">
          <AlertTriangle className="w-8 h-8 text-amber-400/30 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Aucun setup {regionFilter !== "ALL" ? regionFilter : ""} trouvé</p>
          <p className="text-xs text-slate-600 mt-1">No Break = No Trade</p>
        </div>
      ) : (
        <>
          {/* MOMENTUM SECTION */}
          {filtered.momentum.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 px-1">
                <Zap className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-bold text-emerald-400 uppercase tracking-wider">Trades Momentum</span>
                <span className="text-[10px] text-slate-600 bg-slate-800 px-2 py-0.5 rounded-full ml-auto">{filtered.momentum.length}</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {filtered.momentum.map((e, i) => (
                  <SetupCard key={e.ticker} entry={e} onAnalyze={onAnalyze} type="momentum" />
                ))}
              </div>
            </div>
          )}

          {/* SWING SECTION */}
          {filtered.swings.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 px-1">
                <TrendingUp className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-bold text-cyan-400 uppercase tracking-wider">Swings</span>
                <span className="text-[10px] text-slate-600 bg-slate-800 px-2 py-0.5 rounded-full ml-auto">{filtered.swings.length}</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {filtered.swings.map((e, i) => (
                  <SetupCard key={e.ticker} entry={e} onAnalyze={onAnalyze} type="swing" />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Footer */}
      <div className="glass-card rounded-xl p-3 border border-red-500/10 flex items-center gap-2 text-xs text-red-400/70">
        <Shield className="w-4 h-4" />
        <span className="font-bold">{stop_rule}</span>
        <span className="text-slate-700 ml-auto italic">No Break = No Trade</span>
      </div>
    </div>
  );
}
